import { LanguagesEnum } from '@/api/enums';
import { CourseContent } from '@/client/courses';
import { QuizType } from '@/client/quizzes';
import hardcodedTranslations from '@/locales/hardcodedTranslations';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import React from 'react';
import styled from 'styled-components';

const StyledStickyContainer = styled(FlexContainer)`
  padding: var(--default-padding);
  border-top: 1px solid var(--gray-dark);
  position: sticky;
  bottom: calc(-1 * var(--default-padding));
  background-color: var(--beige-main);
  z-index: 1000;
  height: 100px;
  border-radius: 0 0 var(--default-border-radius) var(--default-border-radius);

  div {
    max-width: 1000px;
    justify-content: flex-end;
  }

  button {
    margin-left: auto;
  }
`;

type ContinueOrFinishButtonProps = {
  currentModule: number;
  modules: CourseContent[];
  isCurrentModuleCompleted: boolean;
  isContentStarted: boolean;
  shouldWrapInStickyContainer?: boolean;
  attemptLanguage: LanguagesEnum;
  rtl?: boolean;
  isDisabled?: boolean;
  onContinue: () => void;
  onFinish: () => void;
};

export const ContinueOrFinishButton: React.FC<ContinueOrFinishButtonProps> = ({
  currentModule,
  modules,
  isCurrentModuleCompleted,
  isContentStarted,
  shouldWrapInStickyContainer,
  attemptLanguage,
  rtl,
  isDisabled,
  onContinue,
  onFinish,
}) => {
  const isLastModule = currentModule === modules.length - 1;
  const hasFeedbackModule = modules?.some(
    (module) => module.quiz?.type === QuizType.FEEDBACK,
  );
  // If there's a feedback module, the last non-feedback module should show finish button
  const shouldShowFinishButton = hasFeedbackModule
    ? currentModule === modules.length - 2
    : isLastModule;

  const action = shouldShowFinishButton ? (
    <FlexContainer direction="column" gap={20}>
      {isCurrentModuleCompleted && isContentStarted && (
        <AppButton
          label={hardcodedTranslations.buttons.finish[attemptLanguage]}
          icon="pi pi-check"
          iconPos="right"
          rtl={rtl}
          isDisabled={isDisabled}
          onClick={onFinish}
        />
      )}
    </FlexContainer>
  ) : (
    <FlexContainer direction="column" gap={20}>
      {isCurrentModuleCompleted && isContentStarted && (
        <AppButton
          label={hardcodedTranslations.buttons.continue[attemptLanguage]}
          icon={rtl ? 'pi pi-arrow-left' : 'pi pi-arrow-right'}
          iconPos="right"
          rtl={rtl}
          isDisabled={isDisabled}
          onClick={onContinue}
        />
      )}
    </FlexContainer>
  );

  return shouldWrapInStickyContainer ? (
    <StyledStickyContainer>{action}</StyledStickyContainer>
  ) : (
    action
  );
};
